'use client';
import { useState, useCallback } from 'react';
import { useTranslations as useTranslations$1 } from 'next-intl';
import { Link, usePathname } from '@/i18n/navigation';
import { ArrowRight } from '@/components/ui/icons';
import { useRipple } from '@/hooks/useRipple';
import { useAuth } from '@/hooks/useAuth';
import { useStoredSignupAccess } from '@/hooks/useStoredSignupAccess';
import { SignUpModal } from '@/components/auth';
import { MobileNav } from './MobileNav';
import { hasStoredSignupAccess } from '@/lib/signupAccessStorage';
import { startDevLogin } from '@/lib/devLogin';
import { DOCS_HOME } from '@/lib/docs';
interface NavItem {
    label: string;
    href: string;
    isSection?: boolean;
    /** Renders a plain <a>: the target is an (app) route with no locale prefix,
   *  so the locale-aware Link would send a non-English visitor to a 404. */ unlocalized?: boolean;
}
export function MarketingNav() {
    const t = useTranslations$1();
    const pathname = usePathname();
    const isHomePage = pathname === '/';
    const { createRipple, rippleElements } = useRipple({
        size: 20
    });
    const { isAnonymous, isLoading } = useAuth();
    const isLoggedIn = !isAnonymous && !isLoading;
    const [showSignUpModal, setShowSignUpModal] = useState(false);
    const [modalMode, setModalMode] = useState<'signup' | 'signin'>('signin');
    const hasSignupAccess = useStoredSignupAccess();
    const navItems: NavItem[] = [
        {
            label: t("ZXp0z1"),
            href: '#features',
            isSection: true
        },
        {
            label: t("wNpRl5"),
            href: '#showcase',
            isSection: true
        },
        {
            label: t("5Wwopw"),
            href: '/pricing'
        },
        {
            label: t("_6_sPX"),
            href: DOCS_HOME,
            unlocalized: true
        }
    ];
    const handleSectionClick = useCallback((href: string)=>{
        const sectionId = href.replace('#', '');
        const element = document.getElementById(sectionId);
        if (element) {
            // Navbar is at top-6 (24px) + nav height (~52px) + extra padding (15px)
            const offset = 91;
            const elementPosition = element.getBoundingClientRect().top + window.scrollY;
            const offsetPosition = elementPosition - offset;
            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    }, []);
    const handleDemoClick = (e: React.MouseEvent<HTMLButtonElement>)=>{
        createRipple(e);
        if (hasStoredSignupAccess()) {
            setModalMode('signup');
            setShowSignUpModal(true);
        } else {
            window.open('https://form.typeform.com/to/VVwyeUrj', '_blank', 'noopener,noreferrer');
        }
    };
    // Sign-in from the pricing page returns to it (query included) so a
    // `/pricing?code=X` visitor lands back on the card with the code applied.
    const [returnPath, setReturnPath] = useState('/projects');
    const handleLoginClick = async ()=>{
        const back = pathname === '/pricing' ? `${pathname}${window.location.search}` : '/projects';
        setReturnPath(back);
        if (await startDevLogin(back)) return;
        setModalMode('signin');
        setShowSignUpModal(true);
    };
    return (<>
      { /* Desktop Navigation */ }
      <nav className="fixed left-[50vw] top-6 z-50 -translate-x-1/2">
        <div className="hidden items-center gap-2 rounded-full border border-white/20 bg-white/10 px-6 py-3 shadow-lg backdrop-blur-xl lg:flex">
          {navItems.map((item)=>item.isSection ? (isHomePage ? (<button key={item.href} onClick={()=>handleSectionClick(item.href)} className="rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap text-white/70 transition-all duration-200 hover:bg-white/10 hover:text-white">
                  {item.label}
                </button>) : (<Link key={item.href} href={`/${item.href}`} className="rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap text-white/70 transition-all duration-200 hover:bg-white/10 hover:text-white">
                  {item.label}
                </Link>)) : item.unlocalized ? (<a key={item.href} href={item.href} className="rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap text-white/70 transition-all duration-200 hover:bg-white/10 hover:text-white">
                {item.label}
              </a>) : (<Link key={item.href} href={item.href} className="rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap text-white/70 transition-all duration-200 hover:bg-white/10 hover:text-white">
                {item.label}
              </Link>))}

          { /* Divider */ }
          <div className="mx-1 h-6 w-px bg-white/20"/>

          {isLoggedIn ? (<Link href="/projects" className="group relative flex shrink-0 items-center gap-2 rounded-full bg-white px-5 py-2 text-sm font-semibold text-black transition-all duration-300 hover:scale-[1.02] hover:bg-white/95 active:scale-[0.98] whitespace-nowrap">
              {t("a1Olpu")}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"/>
            </Link>) : (<>
              { /* Login - opens modal in signin mode */ }
              <button onClick={handleLoginClick} className="rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap text-white/70 transition-all duration-200 hover:bg-white/10 hover:text-white">
                {t("odXlk8")}
              </button>

              { /* Primary CTA */ }
              <button onClick={handleDemoClick} className="group relative flex shrink-0 cursor-pointer items-center gap-2 overflow-hidden rounded-full border-0 bg-white px-5 py-2 text-sm font-semibold text-black transition-all duration-300 hover:scale-[1.02] hover:bg-white/95 active:scale-[0.98] whitespace-nowrap">
                <span className="relative z-10 flex items-center gap-2">
                  {hasSignupAccess ? t("8HJxXG") : t("UnR8YN")}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"/>
                </span>
                {rippleElements}
              </button>
            </>)}
        </div>
      </nav>

      { /* Mobile Navigation */ }
      <MobileNav/>

      { /* Sign Up Modal */ }
      <SignUpModal isOpen={showSignUpModal} onClose={()=>setShowSignUpModal(false)} returnPath={returnPath} defaultMode={modalMode} variant="marketing"/>
    </>);
}
